import { stripHTML } from '@candulabs/core';
import { EventTypes } from '@candulabs/craft';
import { colors } from 'canoe';
import throttle from 'lodash/throttle';
import React, { ChangeEvent, useContext, useEffect, useMemo, useRef } from 'react';

import TextEditable from 'craft/components/TextEditable';
import { BuiltInComponents } from 'craft/constants';
import { CollectionContext } from 'craft/contexts';
import { useEditorNode, useEditorSelector, useNodeContext } from 'craft/hooks';
import { Resolver } from 'craft/types';

interface Props {
  childrenString?: string;
  propKey?: string;
  atomicKey?: string;
  onSetProps?: (value: string) => void;
  placeholderSuffix?: string;
  onClick?: () => void;
  fallbackToOriginalValue?: string;
}

const moveCaretToEnd = (el: Node) => {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);

  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
};

// TODO: delete all this logic and just keep the dummy component after we merge the
// RTE normalization PR
export const AtomicText: Resolver<Props> = ({
  childrenString,
  propKey = 'childrenString',
  atomicKey,
  onSetProps,
  placeholderSuffix,
  fallbackToOriginalValue,
  ...props
}: Props) => {
  const { id } = useNodeContext();
  const ref = useRef<any>(null);
  const { setProps } = useEditorNode(id);

  const { id: collectionItemId } = useContext(CollectionContext);

  const [focusedAtomicNode, focusedCraftNode]: [string, string] = useEditorSelector((_state, q) => {
    const focusedElements = q.getEvent(EventTypes.focused);
    return focusedElements.all();
  });

  const isNodeInFocus = useMemo(() => {
    if (focusedAtomicNode && !atomicKey) {
      return focusedAtomicNode === `${id}${collectionItemId ? `-${collectionItemId}` : ``}`;
    }
    return (
      focusedCraftNode === `${id}${collectionItemId ? `-${collectionItemId}` : ``}` &&
      focusedAtomicNode === atomicKey
    );
  }, [focusedCraftNode, focusedAtomicNode, id, collectionItemId, atomicKey]);

  useEffect(() => {
    if (isNodeInFocus) {
      // eslint-disable-next-line no-underscore-dangle
      const dom = ref.current as HTMLInputElement | null;
      if (dom) {
        dom.focus();
        moveCaretToEnd(dom);
      }
    }
  }, [isNodeInFocus]);

  // throttle a bit to reduce the number of calls
  const throttleSetText = throttle(
    (value) => (onSetProps ? onSetProps(value) : setProps({ [propKey]: value })),
    50,
  );
  const onChange = (value: string) => {
    throttleSetText(value);
  };

  if (!isNodeInFocus) {
    let newString = childrenString;
    // if the value is empty and there is a fallback provided, set the fallback value
    if (!childrenString && fallbackToOriginalValue) {
      newString = fallbackToOriginalValue;
      throttleSetText(newString);
    }

    return newString ? (
      <span {...props}>{newString}</span>
    ) : (
      // when empty, show a placeholder to make it clickable
      <span style={{ color: colors.GlitterGrayD20, fontSize: '12px' }} {...props}>
        Double click to edit{placeholderSuffix && ` ${placeholderSuffix}`}
      </span>
    );
  }
  return (
    <TextEditable
      {...props}
      ref={ref}
      data-button-inline
      tagName="span"
      key={id}
      html={childrenString || ''}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(stripHTML(e.target.value))}
    />
  );
};

AtomicText.craft = {
  name: BuiltInComponents.Text,
};
